import type { DirectStorageUploadSessionResponse, UploadProcessingStatus } from '@athwab/shared';
import { getFirebaseAuth } from './firebase';

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';
export const MEDIA_BASE_URL = import.meta.env.VITE_MEDIA_URL ?? 'http://localhost:4100';

interface ApiOptions extends RequestInit {
  skipAuth?: boolean;
}

const DEV_LOGIN_ENABLED =
  !import.meta.env.PROD &&
  ((import.meta.env.VITE_ENABLE_DEV_ADMIN_LOGIN ?? import.meta.env.VITE_ENABLE_DEV_EMAIL_LOGIN ?? 'false').toLowerCase() ===
    'true');

const resolveMethod = (method: string | undefined, body: BodyInit | null | undefined) => {
  if (method) {
    return method.toUpperCase();
  }
  return body == null ? 'GET' : 'POST';
};

const shouldInjectWorkspaceHeaders = (method: string) => !['GET', 'HEAD', 'OPTIONS'].includes(method);

const request = async <T>(baseUrl: string, path: string, options: ApiOptions = {}): Promise<T> => {
  const {
    headers: optionHeaders,
    body,
    skipAuth,
    credentials: credentialsOverride,
    cache: cacheOverride,
    ...rest
  } = options;
  const method = resolveMethod(rest.method, body);
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const headers = new Headers(optionHeaders as HeadersInit | undefined);

  if (shouldInjectWorkspaceHeaders(method)) {
    try {
      const store = (window as unknown as { __workspaceStore?: { getState: () => { orgId: string | null; clientId: string | null; projectId: string | null } } }).__workspaceStore;
      if (store) {
        const state = store.getState();
        if (state.orgId && !headers.has('x-org-id')) {
          headers.set('x-org-id', state.orgId);
        }
        if (state.clientId && !headers.has('x-client-id')) {
          headers.set('x-client-id', state.clientId);
        }
        if (state.projectId && !headers.has('x-project-id')) {
          headers.set('x-project-id', state.projectId);
        }
      }
    } catch {
      // ignore workspace header injection errors
    }
  }

  if (body != null && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const execute = async (requestHeaders: Headers) =>
    fetch(`${baseUrl}${path}`, {
      ...rest,
      method,
      body,
      headers: requestHeaders,
      credentials: credentialsOverride ?? 'include',
      cache: cacheOverride ?? 'no-store'
    });

  let response = await execute(headers);

  if (!skipAuth && response.status === 401) {
    const auth = await getFirebaseAuth();
    const currentUser = auth.currentUser;
    if (currentUser) {
      const retryHeaders = new Headers(headers);
      const token = await currentUser.getIdToken();
      retryHeaders.set('Authorization', `Bearer ${token}`);
      response = await execute(retryHeaders);
    } else if (!DEV_LOGIN_ENABLED) {
      throw new Error('Authentication is required for this request.');
    }
  }

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const cloned = response.clone();
      const text = await cloned.text();
      if (text) {
        try {
          const data = JSON.parse(text) as unknown;
          if (data && typeof data === 'object' && data !== null && 'message' in data && typeof (data as { message: unknown }).message === 'string') {
            if ('details' in data && (data as { details?: unknown }).details !== undefined) {
              message = JSON.stringify({
                message: (data as { message: string }).message,
                details: (data as { details?: unknown }).details
              });
            } else {
              message = (data as { message: string }).message;
            }
          } else {
            message = text;
          }
        } catch {
          message = text;
        }
      }
    } catch {
      // Ignore parsing failures and fall back to default message.
    }
    throw new Error(message);
  }

  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }

  return response.json() as Promise<T>;
};

export const apiFetch = async <T>(path: string, options: ApiOptions = {}) => request<T>(API_BASE_URL, path, options);

export const mediaFetch = async <T>(path: string, options: ApiOptions = {}) =>
  request<T>(MEDIA_BASE_URL, path, options);

const DIRECT_TO_STORAGE_UPLOAD_THRESHOLD_BYTES = 24 * 1024 * 1024;

type UploadResponse = {
  assetUrl: string | null;
  uploadId: string;
  storagePath: string;
  storageBucket: string;
  status: UploadProcessingStatus;
};

const uploadFileThroughApi = async (file: File): Promise<UploadResponse> => {
  const form = new FormData();
  form.append('file', file);
  return request<UploadResponse>(API_BASE_URL, '/uploads/direct', {
    method: 'POST',
    body: form
  });
};

const uploadFileToStorage = async (file: File): Promise<UploadResponse> => {
  const contentType = file.type || 'application/octet-stream';
  const session = await request<DirectStorageUploadSessionResponse>(API_BASE_URL, '/uploads/direct/initiate', {
    method: 'POST',
    body: JSON.stringify({
      filename: file.name,
      contentType,
      sizeBytes: file.size
    })
  });

  const startResponse = await fetch(session.uploadUrl, {
    method: 'POST',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      'Content-Type': session.contentType,
      'x-goog-resumable': 'start'
    }
  });

  if (!startResponse.ok) {
    throw new Error(`Failed to start direct upload (${startResponse.status}).`);
  }

  const resumableUrl = startResponse.headers.get('Location');
  if (!resumableUrl) {
    throw new Error('Direct upload did not return a resumable session URL.');
  }

  const uploadResponse = await fetch(resumableUrl, {
    method: 'PUT',
    credentials: 'omit',
    cache: 'no-store',
    headers: {
      'Content-Type': session.contentType
    },
    body: file
  });

  if (!uploadResponse.ok) {
    throw new Error(`Direct upload failed (${uploadResponse.status}).`);
  }

  return request<UploadResponse>(API_BASE_URL, '/uploads/direct/finalize', {
    method: 'POST',
    body: JSON.stringify({
      uploadId: session.uploadId
    })
  });
};

export const uploadFileDirect = async (
  file: File,
  options?: { waitUntilReady?: boolean }
): Promise<UploadResponse> => {
  const json =
    file.size > DIRECT_TO_STORAGE_UPLOAD_THRESHOLD_BYTES
      ? await uploadFileToStorage(file)
      : await uploadFileThroughApi(file);

  const waitForReady = async (uploadId: string) => {
    const startedAt = Date.now();
    const timeoutMs = 300_000;
    let attempt = 0;

    while (Date.now() - startedAt < timeoutMs) {
      const statusResponse = await request<{
        upload: { status: UploadProcessingStatus; storagePath: string; storageBucket: string; assetUrl: string | null }
      }>(
        API_BASE_URL,
        `/uploads/${uploadId}/status`,
        {
          method: 'GET'
        }
      );
      if (statusResponse.upload.status === 'ready') {
        return statusResponse.upload;
      }
      if (statusResponse.upload.status === 'blocked') {
        throw new Error('Upload was blocked by scanner.');
      }
      const delayMs = Math.min(3_000, 1_000 + attempt * 250);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      attempt += 1;
    }
    throw new Error('Upload is still processing. Please try again in a moment.');
  };

  const shouldWait = options?.waitUntilReady !== false;
  const readyUpload =
    shouldWait && json.status !== 'ready' ? await waitForReady(json.uploadId) : json;
  return {
    assetUrl: readyUpload.assetUrl ?? json.assetUrl ?? null,
    uploadId: json.uploadId,
    storagePath: readyUpload.storagePath ?? json.storagePath,
    storageBucket: readyUpload.storageBucket ?? json.storageBucket,
    status: readyUpload.status
  };
};
